import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { Button, Input, Textarea, Select } from '@components/common'
import { useProjectWizardStore } from '@store/useProjectWizardStore'
import checklistService from '@services/checklistService'
import workflowService from '@services/workflowService'
import '../ProjectWizard.scss'

/**
 * Helper function to safely parse tags (handles string JSON or array)
 */
const parseTags = (tags) => {
	if (Array.isArray(tags)) return tags
	if (typeof tags === 'string') {
		try {
			const parsed = JSON.parse(tags)
			return Array.isArray(parsed) ? parsed : []
		} catch {
			return []
		}
	}
	return []
}

/**
 * Step 5: AI Checklist Generation & Display
 *
 * Shows loading during AI generation, then displays editable checklist
 */
const ChecklistStep = ({ onValidChange, isManual = false }) => {
	const {
		checklist: rawChecklist,
		isGenerating,
		generationError,
		updateChecklistItem,
		addChecklistItem,
		removeChecklistItem,
		reorderChecklist,
		retryGeneration,
	} = useProjectWizardStore()

	// Ensure checklist is always an array to prevent .map() errors
	const checklist = Array.isArray(rawChecklist) ? rawChecklist : []

	const [editingIndex, setEditingIndex] = useState(null)
	const [newItemForm, setNewItemForm] = useState({
		title: '',
		description: '',
		weightage: 1,
		evidence: '',
		workflow_id: '',
		workflow_status: 'not_started', // Default status when created
		tags: [],
	})
	const [showAddForm, setShowAddForm] = useState(false)
	const [tagInput, setTagInput] = useState('')
	const [workflows, setWorkflows] = useState([])
	const [loadingWorkflows, setLoadingWorkflows] = useState(false)

	// Fetch available workflows on mount
	useEffect(() => {
		const fetchWorkflows = async () => {
			setLoadingWorkflows(true)
			try {
				const response = await workflowService.getActive()
				setWorkflows(response.data || [])
			} catch (error) {
				console.error('Error fetching workflows:', error)
			} finally {
				setLoadingWorkflows(false)
			}
		}
		fetchWorkflows()
	}, [])

	// Track previous validity to prevent unnecessary updates
	const prevValidRef = useRef(null)

	// Valid once checklist has items (manual: always not-generating; AI: wait for generation)
	useEffect(() => {
		const isValid = !isGenerating && checklist.length > 0
		// Only call if validity actually changed
		if (prevValidRef.current !== isValid) {
			prevValidRef.current = isValid
			onValidChange && onValidChange(isValid)
		}
	}, [isGenerating, checklist.length, onValidChange])

	// Handle item edit
	const handleEdit = (index) => {
		setEditingIndex(index)
	}

	// Handle save edit
	const handleSaveEdit = (index) => {
		setEditingIndex(null)
	}

	// Handle cancel edit
	const handleCancelEdit = () => {
		setEditingIndex(null)
	}

	// Handle item field change
	const handleItemChange = (index, field, value) => {
		updateChecklistItem(index, { [field]: value })
	}

	// Handle add tag to item
	const handleAddTagToItem = (index, tag) => {
		const item = checklist[index]
		const currentTags = parseTags(item.tags)
		if (tag && !currentTags.includes(tag)) {
			updateChecklistItem(index, { tags: [...currentTags, tag] })
		}
	}

	// Handle remove tag from item
	const handleRemoveTagFromItem = (index, tag) => {
		const item = checklist[index]
		const currentTags = parseTags(item.tags)
		updateChecklistItem(index, { tags: currentTags.filter((t) => t !== tag) })
	}

	// Handle new item form change
	const handleNewItemChange = (field, value) => {
		setNewItemForm((prev) => ({ ...prev, [field]: value }))
	}

	// Handle add new item
	const handleAddItem = () => {
		if (!newItemForm.title.trim()) return

		// Find the selected workflow to get its name
		// Note: workflow_id from form is a string, and API also returns id as string
		const workflowId = newItemForm.workflow_id || null
		const selectedWorkflow = workflowId
			? workflows.find((w) => String(w.id) === String(workflowId))
			: null

		addChecklistItem({
			...newItemForm,
			workflow_id: workflowId ? parseInt(workflowId, 10) : null,
			workflow_name: selectedWorkflow ? selectedWorkflow.name : null,
			workflow_status: 'not_started', // Always set default status when creating
			is_ai_generated: false,
		})

		// Reset form
		setNewItemForm({
			title: '',
			description: '',
			weightage: 1,
			evidence: '',
			workflow_id: '',
			workflow_status: 'not_started',
			tags: [],
		})
		setShowAddForm(false)
	}

	// Handle tag input for new item
	const handleAddTagToNewItem = () => {
		if (tagInput && !newItemForm.tags.includes(tagInput)) {
			setNewItemForm((prev) => ({
				...prev,
				tags: [...prev.tags, tagInput],
			}))
			setTagInput('')
		}
	}

	// Handle remove tag from new item
	const handleRemoveTagFromNewItem = (tag) => {
		setNewItemForm((prev) => ({
			...prev,
			tags: prev.tags.filter((t) => t !== tag),
		}))
	}

	// Handle drag and drop reorder
	const handleDragStart = (e, index) => {
		e.dataTransfer.setData('dragIndex', index.toString())
	}

	const handleDragOver = (e) => {
		e.preventDefault()
	}

	const handleDrop = (e, dropIndex) => {
		e.preventDefault()
		const dragIndex = parseInt(e.dataTransfer.getData('dragIndex'), 10)
		if (dragIndex !== dropIndex) {
			reorderChecklist(dragIndex, dropIndex)
		}
	}

	// Calculate completion stats
	const stats = {
		total: checklist.length,
		completed: checklist.filter((i) =>
			['approved', 'not_applicable'].includes(i.workflow_status)
		).length,
		aiGenerated: checklist.filter((i) => i.is_ai_generated).length,
	}

	// Loading state (AI only)
	if (!isManual && isGenerating) {
		return (
			<div className="wizard-step">
				<div className="wizard-step__header">
					<h2 className="wizard-step__title">Generating Checklist</h2>
					<p className="wizard-step__description">
						AI is analyzing your project scope and generating a comprehensive checklist...
					</p>
				</div>
				<div className="wizard-step__content">
					<div className="checklist-loading">
						<div className="checklist-loading__spinner">
							<div className="spinner spinner--large" />
						</div>
						<div className="checklist-loading__text">
							<h3>Analyzing Compliance Requirements</h3>
							<p>
								This may take a moment as we generate items based on your selected
								standards and project scope.
							</p>
						</div>
						<div className="checklist-loading__steps">
							<div className="loading-step loading-step--complete">
								<i className="icon-check" />
								<span>Reading project details</span>
							</div>
							<div className="loading-step loading-step--active">
								<i className="icon-clock" />
								<span>Analyzing compliance standards</span>
							</div>
							<div className="loading-step">
								<i className="icon-list" />
								<span>Generating checklist items</span>
							</div>
						</div>
					</div>
				</div>
			</div>
		)
	}

	// Error state (AI only)
	if (!isManual && generationError) {
		return (
			<div className="wizard-step">
				<div className="wizard-step__header">
					<h2 className="wizard-step__title">Generation Failed</h2>
					<p className="wizard-step__description">
						Don't worry! You can try generating the checklist again.
					</p>
				</div>
				<div className="wizard-step__content">
					<div className="error-box">
						<i className="icon-ban error-box__icon" />
						<div className="error-box__content">
							<strong>Failed to generate checklist</strong>
							<p>{generationError}</p>
							<div className="error-box__actions">
								<Button variant="primary" onClick={retryGeneration}>
									<i className="icon-refresh" style={{ marginRight: '0.5rem' }} />
									Try Again
								</Button>
							</div>
						</div>
					</div>
					<div className="generation-error__info">
						<h4>What happened?</h4>
						<p>The AI checklist generation encountered an error. This could be due to:</p>
						<ul>
							<li>Temporary network connectivity issues</li>
							<li>AI service being temporarily unavailable</li>
							<li>Invalid project configuration</li>
						</ul>
						<p>
							Click <strong>"Try Again"</strong> to retry the generation, or you can go back
							to review your project details.
						</p>
					</div>
				</div>
			</div>
		)
	}

	return (
		<div className="wizard-step">
			<div className="wizard-step__header">
				<h2 className="wizard-step__title">
					{isManual ? 'Manual Checklist' : 'Compliance Checklist'}
				</h2>
				<p className="wizard-step__description">
					{isManual
						? 'Add and manage your compliance checklist items manually'
						: 'Review and customize the generated checklist items'}
				</p>
			</div>

			<div className="wizard-step__content">
				{/* Stats */}
				<div className="checklist-stats">
					<div className="checklist-stat">
						<span className="checklist-stat__value">{stats.total}</span>
						<span className="checklist-stat__label">Total Items</span>
					</div>
					{!isManual && (
						<div className="checklist-stat">
							<span className="checklist-stat__value">{stats.aiGenerated}</span>
							<span className="checklist-stat__label">AI Generated</span>
						</div>
					)}
					<div className="checklist-stat">
						<span className="checklist-stat__value">
							{checklistService.calculateCompletion(checklist)}%
						</span>
						<span className="checklist-stat__label">Complete</span>
					</div>
				</div>

				{/* Empty state prompt for manual mode */}
				{isManual && checklist.length === 0 && (
					<div className="info-box info-box--primary" style={{ marginBottom: '1rem' }}>
						<i className="icon-list info-box__icon" />
						<div className="info-box__content">
							<strong>No items yet</strong>
							<p>Use the "Add New Item" button below to start building your checklist.</p>
						</div>
					</div>
				)}

				{/* Checklist Table */}
				<div className="checklist-table-wrapper">
					<table className="checklist-table">
						<thead>
							<tr>
								<th style={{ width: '40px' }}>#</th>
								<th>Description</th>
								<th style={{ width: '100px' }}>Weightage</th>
								<th>Evidence</th>
								<th style={{ width: '150px' }}>Workflow</th>
								<th>Tags</th>
								<th style={{ width: '100px' }}>Actions</th>
							</tr>
						</thead>
						<tbody>
							{checklist.map((item, index) => (
								<tr
									key={item.id || index}
									draggable
									onDragStart={(e) => handleDragStart(e, index)}
									onDragOver={handleDragOver}
									onDrop={(e) => handleDrop(e, index)}
									className={editingIndex === index ? 'editing' : ''}
								>
									<td className="checklist-table__order">
										<i className="icon-menu drag-handle" />
										{index + 1}
									</td>
									<td>
										{editingIndex === index ? (
											<div className="inline-edit">
												<Input
													value={item.title}
													onChange={(e) =>
														handleItemChange(index, 'title', e.target.value)
													}
													placeholder="Title"
												/>
												<Textarea
													value={item.description || ''}
													onChange={(e) =>
														handleItemChange(index, 'description', e.target.value)
													}
													placeholder="Description"
													rows={2}
												/>
											</div>
										) : (
											<div className="checklist-item__content">
												{item.control_reference && (
													<span className="control-ref">{item.control_reference}</span>
												)}
												<strong>{item.title}</strong>
												{item.description && <p>{item.description}</p>}
												{item.is_ai_generated && (
													<span className="ai-badge">AI</span>
												)}
											</div>
										)}
									</td>
									<td>
										{editingIndex === index ? (
											<Input
												type="number"
												min="1"
												max="100"
												value={item.weightage}
												onChange={(e) =>
													handleItemChange(index, 'weightage', parseInt(e.target.value, 10) || 1)
												}
											/>
										) : (
											<span className="weightage-badge">{item.weightage}</span>
										)}
									</td>
									<td>
										{editingIndex === index ? (
											<Textarea
												value={item.evidence || ''}
												onChange={(e) =>
													handleItemChange(index, 'evidence', e.target.value)
												}
												placeholder="Required evidence..."
												rows={2}
											/>
										) : (
											<span className="evidence-text">{item.evidence || '-'}</span>
										)}
									</td>
									<td>
										{editingIndex === index ? (
											<Select
												options={[
													{ value: '', label: 'Select Workflow' },
													...workflows.map((w) => ({
														value: String(w.id),
														label: w.name,
													})),
												]}
												value={item.workflow_id?.toString() || ''}
												onChange={(e) => {
													const workflowIdStr = e.target.value || null
													const selectedWorkflow = workflowIdStr
														? workflows.find((w) => String(w.id) === workflowIdStr)
														: null
													handleItemChange(index, 'workflow_id', workflowIdStr ? parseInt(workflowIdStr, 10) : null)
													handleItemChange(index, 'workflow_name', selectedWorkflow ? selectedWorkflow.name : null)
												}}
												disabled={loadingWorkflows}
											/>
										) : (
											<span
												className="workflow-name"
												title={item.workflow_name || 'No workflow selected'}
												style={{ color: item.workflow_name ? 'inherit' : '#888', fontStyle: item.workflow_name ? 'normal' : 'italic' }}
											>
												{item.workflow_name || 'Not assigned'}
											</span>
										)}
									</td>
									<td>
										<div className="tag-cell">
											{parseTags(item.tags).map((tag) => (
												<span key={tag} className="tag tag--small">
													{tag}
													{editingIndex === index && (
														<button
															type="button"
															className="tag__remove"
															onClick={() => handleRemoveTagFromItem(index, tag)}
														>
															<i className="icon-close" />
														</button>
													)}
												</span>
											))}
											{editingIndex === index && (
												<input
													type="text"
													className="tag-input-inline"
													placeholder="+ Tag"
													onKeyDown={(e) => {
														if (e.key === 'Enter' && e.target.value) {
															handleAddTagToItem(index, e.target.value)
															e.target.value = ''
														}
													}}
												/>
											)}
										</div>
									</td>
									<td>
										<div className="action-buttons">
											{editingIndex === index ? (
												<>
													<Button
														variant="ghost"
														size="small"
														onClick={() => handleSaveEdit(index)}
														title="Save"
													>
														<i className="icon-check" />
													</Button>
													<Button
														variant="ghost"
														size="small"
														onClick={handleCancelEdit}
														title="Cancel"
													>
														<i className="icon-close" />
													</Button>
												</>
											) : (
												<>
													<Button
														variant="ghost"
														size="small"
														onClick={() => handleEdit(index)}
														title="Edit"
													>
														<i className="icon-pencil" />
													</Button>
													<Button
														variant="ghost"
														size="small"
														onClick={() => removeChecklistItem(index)}
														title="Delete"
														className="danger"
													>
														<i className="icon-trash" />
													</Button>
												</>
											)}
										</div>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>

				{/* Add New Item */}
				{showAddForm ? (
					<div className="add-item-form">
						<h4>Add New Checklist Item</h4>
						<div className="form-grid">
							<div className="form-group form-group--full">
								<Input
									label="Title"
									value={newItemForm.title}
									onChange={(e) => handleNewItemChange('title', e.target.value)}
									placeholder="Enter item title..."
									required
								/>
							</div>
							<div className="form-group form-group--full">
								<Textarea
									label="Description"
									value={newItemForm.description}
									onChange={(e) => handleNewItemChange('description', e.target.value)}
									placeholder="Enter detailed description..."
									rows={2}
								/>
							</div>
							<div className="form-group">
								<Input
									type="number"
									label="Weightage"
									min="1"
									max="100"
									value={newItemForm.weightage}
									onChange={(e) =>
										handleNewItemChange('weightage', parseInt(e.target.value, 10) || 1)
									}
								/>
							</div>
							<div className="form-group">
								<Select
									label="Workflow"
									options={[
										{ value: '', label: loadingWorkflows ? 'Loading...' : 'Select Workflow' },
										...workflows.map((w) => ({
											value: w.id.toString(),
											label: `${w.name}${w.steps_count ? ` (${w.steps_count} steps)` : ''}`,
										})),
									]}
									value={newItemForm.workflow_id}
									onChange={(e) => handleNewItemChange('workflow_id', e.target.value)}
									disabled={loadingWorkflows}
								/>
							</div>
							<div className="form-group form-group--full">
								<Textarea
									label="Required Evidence"
									value={newItemForm.evidence}
									onChange={(e) => handleNewItemChange('evidence', e.target.value)}
									placeholder="What evidence is required..."
									rows={2}
								/>
							</div>
							<div className="form-group form-group--full">
								<label className="form-label">Tags</label>
								<div className="tag-input-wrapper">
									<div className="tag-input-field">
										<Input
											placeholder="Add tag..."
											value={tagInput}
											onChange={(e) => setTagInput(e.target.value)}
											onKeyDown={(e) => {
												if (e.key === 'Enter') {
													e.preventDefault()
													handleAddTagToNewItem()
												}
											}}
										/>
										<Button
											variant="outline"
											size="small"
											onClick={handleAddTagToNewItem}
											disabled={!tagInput}
										>
											Add
										</Button>
									</div>
									{newItemForm.tags.length > 0 && (
										<div className="tag-list">
											{newItemForm.tags.map((tag) => (
												<span key={tag} className="tag">
													{tag}
													<button
														type="button"
														className="tag__remove"
														onClick={() => handleRemoveTagFromNewItem(tag)}
													>
														<i className="icon-close" />
													</button>
												</span>
											))}
										</div>
									)}
								</div>
							</div>
						</div>
						<div className="add-item-form__actions">
							<Button variant="outline" onClick={() => setShowAddForm(false)}>
								Cancel
							</Button>
							<Button
								variant="primary"
								onClick={handleAddItem}
								disabled={!newItemForm.title.trim()}
							>
								Add Item
							</Button>
						</div>
					</div>
				) : (
					<Button
						variant="outline"
						onClick={() => setShowAddForm(true)}
						className="add-item-button"
					>
						<i className="icon-plus" /> Add New Item
					</Button>
				)}
			</div>
		</div>
	)
}

ChecklistStep.propTypes = {
	onValidChange: PropTypes.func,
	isManual: PropTypes.bool,
}

export default ChecklistStep
